import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, MapPin } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function PropertiesList() {
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    useEffect(() => { api.get("/properties?limit=100").then(({ data }) => { setRows(data.items || []); setTotal(data.total || 0); }); }, []);

    return (
        <div className="p-6 md:p-10" data-testid="page-properties">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <div className="overline">Properties</div>
                    <h1 className="mt-2 font-display text-4xl tracking-tight leading-none">Portfolio</h1>
                    <div className="text-sm text-muted-foreground mt-2">{total} total</div>
                </div>
                <Link to="/app/properties/new" className="bg-foreground text-background px-4 py-2 text-sm inline-flex items-center gap-2 hover:opacity-90" data-testid="new-property-btn">
                    <Plus size={14} /> Add Property
                </Link>
            </div>
            {rows.length === 0 && (
                <div className="border border-border p-8 text-sm text-muted-foreground" data-testid="properties-empty">
                    No properties yet. <Link to="/app/properties/new" className="underline">Add your first property</Link>.
                </div>
            )}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {rows.map((p) => (
                    <Link key={p.id} to={`/app/properties/${p.id}`} className="border border-border p-6 hover:bg-secondary/40 transition-colors" data-testid={`property-card-${p.id}`}>
                        <div className="overline flex items-center gap-1"><MapPin size={10} /> {p.property_type || "Property"}</div>
                        <div className="mt-3 font-display text-xl tracking-tight leading-tight">{p.address_line1}</div>
                        <div className="mt-1 text-sm text-muted-foreground">{[p.city, p.state, p.postal_code].filter(Boolean).join(", ")}</div>
                        <div className="mt-4 grid grid-cols-2 gap-3 text-xs">
                            {p.lot_size_sqft ? <div><div className="overline">Lot sqft</div>{p.lot_size_sqft}</div> : null}
                            {p.building_size_sqft ? <div><div className="overline">Bldg sqft</div>{p.building_size_sqft}</div> : null}
                            {p.year_built ? <div><div className="overline">Year built</div>{p.year_built}</div> : null}
                            {p.zoning ? <div><div className="overline">Zoning</div>{p.zoning}</div> : null}
                        </div>
                    </Link>
                ))}
            </div>
        </div>
    );
}

export function PropertyNew() {
    const [form, setForm] = useState({ address_line1: "", city: "", state: "", postal_code: "", property_type: "", lot_size_sqft: "", building_size_sqft: "", year_built: "", zoning: "", notes: "" });
    const [suggestions, setSuggestions] = useState([]);
    const nav = useNavigate();

    const suggest = async (q) => {
        if (!q || q.length < 2) return setSuggestions([]);
        try {
            const { data } = await api.get(`/properties/address-suggest?q=${encodeURIComponent(q)}`);
            setSuggestions(data.suggestions || []);
        } catch { setSuggestions([]); }
    };

    useEffect(() => {
        const t = setTimeout(() => suggest(form.address_line1), 300);
        return () => clearTimeout(t);
    }, [form.address_line1]);

    const submit = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                ...form,
                lot_size_sqft: form.lot_size_sqft ? Number(form.lot_size_sqft) : null,
                building_size_sqft: form.building_size_sqft ? Number(form.building_size_sqft) : null,
                year_built: form.year_built ? Number(form.year_built) : null,
            };
            const { data } = await api.post("/properties", payload);
            toast.success("Property added");
            nav(`/app/properties/${data.id}`);
        } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    };

    return (
        <div className="p-6 md:p-10" data-testid="page-property-new">
            <Link to="/app/properties" className="text-xs text-muted-foreground hover:opacity-70">← Back to properties</Link>
            <h1 className="mt-4 font-display text-4xl tracking-tight leading-none">New property</h1>
            <div className="text-sm text-muted-foreground mt-2">Address autocomplete is a placeholder; enter address manually for accurate records.</div>

            <form onSubmit={submit} className="max-w-3xl mt-8 space-y-5" data-testid="property-form">
                <div>
                    <Label>Address line 1</Label>
                    <div className="relative mt-2">
                        <Input value={form.address_line1} onChange={(e) => setForm({ ...form, address_line1: e.target.value })} required data-testid="property-address-line1" />
                        {suggestions.length > 0 && (
                            <div className="absolute left-0 right-0 top-full mt-1 border border-border bg-background z-10 max-h-56 overflow-y-auto" data-testid="address-suggestions">
                                {suggestions.map((s, i) => (
                                    <button
                                        key={i} type="button"
                                        onClick={() => { setForm({ ...form, address_line1: s.address_line1, city: s.city, state: s.state, postal_code: s.postal_code }); setSuggestions([]); }}
                                        className="w-full text-left px-3 py-2 text-sm hover:bg-secondary border-b border-border last:border-b-0"
                                        data-testid={`address-suggest-${i}`}
                                    >
                                        {s.address_line1}, {s.city}, {s.state} {s.postal_code}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                    <div><Label>City</Label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="mt-2" data-testid="property-city" /></div>
                    <div><Label>State</Label><Input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} className="mt-2" data-testid="property-state" /></div>
                    <div><Label>Postal code</Label><Input value={form.postal_code} onChange={(e) => setForm({ ...form, postal_code: e.target.value })} className="mt-2" data-testid="property-postal" /></div>
                </div>
                <div>
                    <Label>Property type</Label>
                    <select value={form.property_type} onChange={(e) => setForm({ ...form, property_type: e.target.value })} className="mt-2 w-full border border-border bg-background px-3 py-2 text-sm" data-testid="property-type">
                        <option value="">— select —</option>
                        {["Single-family", "Multi-family", "Townhouse", "Land", "Commercial", "Industrial", "Mixed-use", "Tax deed", "Tax lien"].map((t) => <option key={t}>{t}</option>)}
                    </select>
                </div>
                <div className="grid grid-cols-3 gap-4">
                    <div><Label>Lot sqft</Label><Input type="number" value={form.lot_size_sqft} onChange={(e) => setForm({ ...form, lot_size_sqft: e.target.value })} className="mt-2" data-testid="property-lot-sqft" /></div>
                    <div><Label>Building sqft</Label><Input type="number" value={form.building_size_sqft} onChange={(e) => setForm({ ...form, building_size_sqft: e.target.value })} className="mt-2" data-testid="property-bldg-sqft" /></div>
                    <div><Label>Year built</Label><Input type="number" value={form.year_built} onChange={(e) => setForm({ ...form, year_built: e.target.value })} className="mt-2" data-testid="property-year" /></div>
                </div>
                <div><Label>Zoning</Label><Input value={form.zoning} onChange={(e) => setForm({ ...form, zoning: e.target.value })} className="mt-2" data-testid="property-zoning" /></div>
                <div><Label>Notes</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-2" /></div>
                <div className="pt-4 border-t border-border">
                    <button type="submit" className="bg-foreground text-background px-6 py-3 text-sm hover:opacity-90" data-testid="property-save">Create property</button>
                </div>
            </form>
        </div>
    );
}
